/**
 * Hook for searching plays by their callChips content.
 *
 * Provides imperative search functions (not automatic query-on-mount)
 * since chip search is triggered by user actions like clicking
 * "find plays using this vocabulary entry".
 */

import { useState, useCallback } from 'react'
import {
	searchPlaysByVocabularyId,
	searchPlaysByChipText,
	type PlayChipSearchResult,
} from '@/api/queries/PlayChipQueries'

// ============================================================================
// Types
// ============================================================================

export interface UsePlayChipSearchReturn {
	results: PlayChipSearchResult[]
	isSearching: boolean
	error: Error | null
	searchByVocabularyId: (vocabularyId: string) => Promise<void>
	searchByText: (text: string) => Promise<void>
	clearResults: () => void
}

// ============================================================================
// Hook
// ============================================================================

export function usePlayChipSearch(playbookId: string): UsePlayChipSearchReturn {
	const [results, setResults] = useState<PlayChipSearchResult[]>([])
	const [isSearching, setIsSearching] = useState(false)
	const [error, setError] = useState<Error | null>(null)

	const searchByVocabularyId = useCallback(async (vocabularyId: string) => {
		try {
			setIsSearching(true)
			setError(null)
			const plays = await searchPlaysByVocabularyId(playbookId, vocabularyId)
			setResults(plays)
		} catch (err) {
			setError(err instanceof Error ? err : new Error('Search failed'))
			setResults([])
		} finally {
			setIsSearching(false)
		}
	}, [playbookId])

	const searchByText = useCallback(async (text: string) => {
		try {
			setIsSearching(true)
			setError(null)
			const plays = await searchPlaysByChipText(playbookId, text)
			setResults(plays)
		} catch (err) {
			setError(err instanceof Error ? err : new Error('Search failed'))
			setResults([])
		} finally {
			setIsSearching(false)
		}
	}, [playbookId])

	const clearResults = useCallback(() => {
		setResults([])
		setError(null)
	}, [])

	return {
		results,
		isSearching,
		error,
		searchByVocabularyId,
		searchByText,
		clearResults,
	}
}
